import { createContext, ReactNode, useEffect, useMemo, useReducer } from "react";
import appSlice, { initialState, setAuthHeaderSet, setLoggedinUser } from "./appSlice";
import { ContextProps } from "./types";
import useAppActions from "./useAppActions";
import { RelativeComponentLoader } from "@/components/loader";
import { getUser } from "@/services/auth";
import { setAuthorizationHeader } from "@/helpers/api_helper";
import { posthogIdentify, sentrySetUser, rollbarSetUser } from "@/services/logging";
import useLocationTracker from "./useLocationTracker";

export const AppContext = createContext<ContextProps>({
  state: initialState,
  dispatch: () => null,
});

const AppProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [state, dispatch] = useReducer(appSlice.reducer, appSlice.getInitialState());

  useLocationTracker({ dispatch });

  const {
    fetchTenantInfo,
    loadingTenantInfo,
    setLoadingTenantInfo,
    fetchDatastores,
    loadingDatastores,
    setLoadingDatastores,
    fetchCompanies,
  } = useAppActions(dispatch);

  useEffect(() => {
    const userFromLocalStorage = getUser();
    if (!userFromLocalStorage) {
      setLoadingTenantInfo(false);
      setLoadingDatastores(false);
      return;
    }
    // if app context does not have loggedin user, set it from local storage
    if (!state.loggedinUser) {
      dispatch(setLoggedinUser(userFromLocalStorage));
      posthogIdentify(userFromLocalStorage);
      sentrySetUser(userFromLocalStorage);
      rollbarSetUser(userFromLocalStorage);
      return;
    }

    if (state.loggedinUser) {
      setAuthorizationHeader(state.loggedinUser.auth_token);
      dispatch(setAuthHeaderSet(true));
      fetchTenantInfo();
      fetchDatastores();
      fetchCompanies();
    }
  }, [state.loggedinUser]);

  const values = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch]
  );

  return (
    <AppContext.Provider value={values}>
      {loadingTenantInfo || loadingDatastores ? (
        <RelativeComponentLoader label={"Loading..."} />
      ) : (
        children
      )}
    </AppContext.Provider>
  );
};

export default AppProvider;
